<script setup>
import { i18n } from 'boot/i18n';
import {
  HrConfirmBtn,
  HrDateField,
  HrDismissBtn,
  HrFormDialog,
  HrListSelect,
  HrTextField,
} from 'src/design-system';
import { useCountries } from 'src/composables/countries';
import { computed, ref } from 'vue';

import { bodyHasExpiry, bodyHasJurisdiction } from '../../domain/constants/credentialingBodies';
import {
  credentialingBodyOptions,
  levelOptionsFor,
  numberLabelFor,
} from '../presenters/credentialingOptions';
import { CREDENTIAL_STANDING, isSameCredential, windowsOverlap } from '../../domain/model/Credential';

/**
 * Records a certification or license for the period it was valid.
 *
 * AN ORDINARY FORM DIALOG, NOT THE VERSIONED ONE. `HrVersionedFormDialog` exists for records whose
 * versions do not overlap and may have holes, where "from when does this take effect" is a judgement
 * the dialog has to ask for. A credential arrives with both dates printed on the certificate, so
 * there is nothing to ask: it is two date fields on an ordinary form.
 *
 * RENEWING OPENS THIS EMPTY BUT FOR THE IDENTITY. A renewal is a new record carrying its own window,
 * not an edit of the previous one, so the body and the number come across and the dates do not. That
 * is what keeps the old window readable for a date of service inside it.
 *
 * THERE IS NO STATUS FIELD. Whether a credential is current follows from its dates, so a control
 * asking somebody to say it as well would be a second answer that comes to disagree with the first.
 * The row says what is current, and the panel files it under the right heading on its own.
 *
 * OVERLAP IS REFUSED, and it is the same rule the old editor enforced as "one active per
 * organization", read off the dates instead of a flag. One certificate cannot be valid twice over
 * the same day; a renewal picking up the day after the last one ends is fine and ordinary.
 */
defineOptions({ name: 'EditCredentialDialog' });

const props = defineProps({
  /** The credential being edited, or null to add one. */
  credential: {
    type: Object,
    default: null,
  },
  /** The person's other credentials, for the overlap check. */
  existing: {
    type: Array,
    default: () => [],
  },
  /**
   * Where the person lives, as a starting guess for which state licensed them.
   *
   * A GUESS, NOT A FACT. Most people are licensed where they work, so offering it saves the commonest
   * answer, and somebody licensed in two states changes it. It is only ever a starting value: an
   * existing record opens on what was recorded, never on this.
   */
  defaultJurisdiction: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({ ok: null });

const dialog = ref(null);
const draft = ref(null);

const bodyOptions = credentialingBodyOptions();

/**
 * The states, from the one list the rest of the product uses.
 *
 * A typed state would be a second spelling of something the platform already has a canonical list
 * for, and the code is part of a license's identity — so "CA", "Ca" and "California" would read as
 * three different licenses held at once.
 */
const { countryStates } = useCountries();
const jurisdictionOptions = countryStates('US')
  .map((state) => ({ value: state.code, label: state.label }));

/**
 * Only when it is a state this list actually holds.
 *
 * The person's address state is whatever their country uses, and the license list is US states, so a
 * clinic in Australia would otherwise seed a value no option matches and the field would render
 * blank while holding something.
 */
const knownJurisdiction = (value) => {
  return jurisdictionOptions.some((option) => option.value === value) ? value : null;
};

/**
 * The person's own state, but ONLY where the issuer is a state board.
 *
 * Seeding it regardless looked harmless because the field is hidden for other issuers, and it was
 * not: the state is part of a credential's IDENTITY, so a BACB certificate quietly carrying "CA"
 * fails to match the same certificate recorded without one. The overlap check is skipped, two
 * overlapping periods of one certificate are accepted, and the row reads "BCBA · CA" for a body that
 * has no state at all.
 */
const defaultJurisdictionFor = (bodyId) => {
  return bodyHasJurisdiction(bodyId) ? knownJurisdiction(props.defaultJurisdiction) : null;
};

const blank = () => ({
  organization: null,
  level: null,
  certificateId: null,
  jurisdiction: null,
  // Good standing is the ordinary case, so it is the value a new record opens on and nobody has to
  // answer a question about a credential that is simply fine.
  status: CREDENTIAL_STANDING.ACTIVE,
  startDate: null,
  expirationDate: null,
  // Who checked it and how. An auditor asks this of every credential a modifier was billed against,
  // so it is part of the record rather than a note somebody keeps elsewhere.
  verification: { verifiedOn: null, method: null },
});

const levelOptions = computed(() => levelOptionsFor(draft.value?.organization));
const wantsExpiry = computed(() => bodyHasExpiry(draft.value?.organization));
const wantsJurisdiction = computed(() => bodyHasJurisdiction(draft.value?.organization));

// A state grants a license and a board grants a certificate; asking for a "certificate number" on a
// license asks for something the document in front of the reader does not call itself.
const standingOptions = Object.values(CREDENTIAL_STANDING)
  .map((value) => ({ value, label: i18n.t(`staff.qualifications.standings.${value}`) }));

const numberLabel = computed(() => numberLabelFor(draft.value?.organization));

/**
 * The one thing that can be wrong in a way the fields cannot show on their own.
 *
 * Checked against the person's OTHER records only: a credential does not overlap itself, and
 * comparing it to itself would make every edit unsaveable.
 */
const overlapping = computed(() => {
  if (draft.value === null) {
    return false;
  }

  return props.existing
    .filter((entry) => entry !== props.credential)
    .filter((entry) => isSameCredential(entry, draft.value))
    .some((entry) => windowsOverlap(entry, draft.value));
});

/**
 * The expiry is REQUIRED where the body issues something that lapses, which is every body but the
 * NPI registry. An open-ended certification is not a real answer here: certifications are renewed on
 * a cycle, so a missing end reads as "never expires" and would keep billing on a credential that
 * lapsed years ago. It is left optional only where the body genuinely issues nothing that expires.
 */
const canConfirm = computed(() => {
  const hasExpiry = !wantsExpiry.value || typeof draft.value?.expirationDate === 'string';

  const hasJurisdiction = !wantsJurisdiction.value || typeof draft.value?.jurisdiction === 'string';

  return draft.value?.organization !== null
    && typeof draft.value?.startDate === 'string'
    && hasExpiry
    && hasJurisdiction
    && !overlapping.value;
});

/**
 * The two dates bound each other, so the calendar cannot offer a day that makes the window run
 * backwards. `hr-date` speaks 'YYYY/MM/DD' rather than the model's form, and zero-padded calendar
 * strings sort lexicographically, which is what lets these be string compares.
 */
const asPickerDate = (date) => (typeof date === 'string' ? date.replaceAll('-', '/') : null);

const startSelectable = computed(() => {
  const latest = asPickerDate(draft.value?.expirationDate);

  return latest === null ? null : (date) => date <= latest;
});

const expirySelectable = computed(() => {
  const earliest = asPickerDate(draft.value?.startDate);

  return earliest === null ? null : (date) => date >= earliest;
});

/** Opens to add, to edit, or to renew — which is adding with the identity already filled in. */
function show({ credential = null, renewing = null } = {}) {
  if (renewing !== null) {
    draft.value = {
      ...blank(),
      organization: renewing.organization,
      level: renewing.level,
      certificateId: renewing.certificateId,
      jurisdiction: renewing.jurisdiction,
      // Not carried across: a renewal is a different certificate period and has not been checked
      // yet. Copying the old verification would assert somebody had looked at a document nobody
      // has seen.
    };
  } else {
    draft.value = credential === null
      ? blank()
      : { ...credential, verification: { ...(credential.verification ?? blank().verification) } };
  }

  dialog.value?.show();
} // show()

function hide() {
  dialog.value?.hide();
} // hide()

/**
 * Changing the body clears what only that body had, and re-offers what the person brings with them.
 *
 * A level from another body would otherwise survive the change and sit invisibly on a record whose
 * form no longer shows the field, and a state is part of a credential's identity, so a stale one
 * makes two records look like one.
 *
 * The state resets to the PERSON'S OWN rather than to nothing, which the previous version got wrong:
 * the body is the first field anybody touches on a new record, so clearing to null wiped the default
 * before it was ever seen, and the field it was meant to fill appeared empty every time.
 */
function onBodyChange(value) {
  draft.value.organization = value;
  draft.value.level = null;
  draft.value.jurisdiction = defaultJurisdictionFor(value);
} // onBodyChange()

function onSubmit() {
  if (!canConfirm.value) {
    return;
  }

  emit('ok', { ...draft.value });
  hide();
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="i18n.t('staff.qualifications.titleCredentialDialog')"
    data-e2e="dialog-edit-staff-credential"
    @submit="onSubmit"
  >
    <div
      v-if="draft !== null"
      class="row q-col-gutter-md"
    >
      <hr-list-select
        class="col-12"
        :model-value="draft.organization"
        :options="bodyOptions"
        :label="i18n.t('staff.qualifications.inputBody')"
        data-e2e="select-staff-credential-body"
        @update:model-value="onBodyChange"
      />

      <!-- BEFORE the level, because each answer narrows the next: the issuer decides whether there
           is a state at all, and the state will decide which levels exist once they differ by state.
           Asking for the level first would offer options that depend on an answer not yet given.
           When the levels do become per-state, changing the state has to clear the level here — the
           same way changing the issuer already does. -->
      <hr-list-select
        v-if="wantsJurisdiction"
        v-model="draft.jurisdiction"
        class="col-12"
        :options="jurisdictionOptions"
        :label="i18n.t('staff.qualifications.inputJurisdiction')"
        :hint="i18n.t('staff.qualifications.hintJurisdiction')"
        use-input
        data-e2e="select-staff-credential-jurisdiction"
      />

      <hr-list-select
        v-if="levelOptions.length > 0"
        v-model="draft.level"
        class="col-12"
        :options="levelOptions"
        :label="i18n.t('staff.qualifications.inputLevel')"
        clearable
        data-e2e="select-staff-credential-level"
      />

      <hr-text-field
        v-model="draft.certificateId"
        class="col-12"
        :label="numberLabel"
        data-e2e="input-staff-credential-number"
      />

      <!-- The field this record exists for: without it nothing can be answered about a past date. -->
      <hr-date-field
        v-model="draft.startDate"
        class="col-12 col-sm-6"
        :label="i18n.t('staff.qualifications.inputStartDate')"
        :options="startSelectable"
        data-e2e="input-staff-credential-start"
      />

      <!-- Always shown, even for the one issuer that grants nothing expiring: a field that appears
           and disappears as the issuer changes is harder to read than an empty one, and leaving it
           empty says the same thing. It is REQUIRED only where the issuer does grant an expiry. -->
      <hr-date-field
        v-model="draft.expirationDate"
        class="col-12 col-sm-6"
        :label="i18n.t('staff.qualifications.inputExpirationDate')"
        :options="expirySelectable"
        :error="overlapping"
        :error-message="i18n.t('staff.qualifications.errorOverlap')"
        data-e2e="input-staff-credential-expiry"
      />

      <!-- What the ISSUER has done, which no date can answer: a suspended license reads as open on
           the calendar and may not be practiced on. -->
      <hr-list-select
        v-model="draft.status"
        class="col-12"
        :options="standingOptions"
        :label="i18n.t('staff.qualifications.inputStanding')"
        data-e2e="select-staff-credential-standing"
      />

      <hr-date-field
        v-model="draft.verification.verifiedOn"
        class="col-12 col-sm-6"
        :label="i18n.t('staff.qualifications.inputVerifiedOn')"
        clearable
        data-e2e="input-staff-credential-verified-on"
      />

      <hr-text-field
        v-model="draft.verification.method"
        class="col-12 col-sm-6"
        :label="i18n.t('staff.qualifications.inputVerifiedMethod')"
        data-e2e="input-staff-credential-verified-how"
      />
    </div>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="i18n.t('common.buttonSave')"
        type="submit"
        :disable="!canConfirm"
        data-e2e="btn-save-staff-credential"
      />
    </template>
  </hr-form-dialog>
</template>
